import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { backendClient } from '../../../api/backend';

/**
 * Custom hook for CLM Dashboard data fetching.
 * Fires all API calls independently for progressive loading —
 * each widget renders as soon as its data arrives.
 */
const useDashboardData = () => {
  const user = useSelector((state) => state.auth.user?.user);

  // Each data source has independent loading state
  const [lifecycle, setLifecycle] = useState({ loading: true, data: null });
  const [smartViewCounts, setSmartViewCounts] = useState({ loading: true, data: null });
  const [alertSummary, setAlertSummary] = useState({ loading: true, data: null });
  const [deadlines, setDeadlines] = useState({ loading: true, data: null });
  const [sigPerformance, setSigPerformance] = useState({ loading: true, data: null });
  const [topParties, setTopParties] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!user?.id) return;

    // 1. Lifecycle dashboard (stages, recent work, action items)
    backendClient
      .getLifecycleDashboard(user.id)
      .then((data) => setLifecycle({ loading: false, data }))
      .catch(() => setLifecycle({ loading: false, data: null }));

    // 2. Smart view counts (expiring_30, auto_renewing, etc.)
    backendClient
      .get('api/contract/smart-view-counts')
      .then((res) => res.json())
      .then((data) => setSmartViewCounts({ loading: false, data }))
      .catch(() => setSmartViewCounts({ loading: false, data: null }));

    // 3. Alert summary (unreviewed regulatory alerts)
    backendClient
      .getAlertSummary()
      .then((data) => setAlertSummary({ loading: false, data }))
      .catch(() => setAlertSummary({ loading: false, data: null }));

    // 4. Upcoming deadlines
    backendClient
      .getDashboardUpcomingDeadlines()
      .then((data) => setDeadlines({ loading: false, data }))
      .catch(() => setDeadlines({ loading: false, data: null }));

    // 5. Signature performance
    backendClient
      .getDashboardSignaturePerformance()
      .then((data) => setSigPerformance({ loading: false, data }))
      .catch(() => setSigPerformance({ loading: false, data: null }));

    // 6. Top counterparties
    backendClient
      .getDashboardTopCounterparties()
      .then((data) => setTopParties({ loading: false, data }))
      .catch(() => setTopParties({ loading: false, data: null }));
  }, [user?.id]);

  return {
    lifecycle,
    smartViewCounts,
    alertSummary,
    deadlines,
    sigPerformance,
    topParties,
  };
};

export default useDashboardData;
